import React, { useMemo } from 'react';
import StatTile from '../StatTile';
import { Sparkline, formatNumber } from '../charts';
import { Badge, Icon, ProgressBar } from '../ui';
import {
  HEALTH_LABEL, HEALTH_TONE, KIND_GROUP_LABEL, THRESHOLDS, ago, healthWithReasons,
} from '../../observability/contract';
import ArchitectureMap from './ArchitectureMap';
import { rate } from './units';

const KIND_ORDER = ['proxy', 'bridge', 'core'];
const KIND_LEDE = {
  proxy: 'the gateways every agent talks through',
  bridge: 'channel connectors, one per messaging platform',
  core: 'control plane and admin console',
};

export function HealthBadge({ health }) {
  return <Badge tone={HEALTH_TONE[health] || 'neutral'} dot>{HEALTH_LABEL[health] || health}</Badge>;
}

function UsageBar({ label, value, warn, crit }) {
  const v = value == null ? null : Number(value);
  const tone = v == null ? undefined : v >= crit ? 'danger' : v >= warn ? 'warn' : 'ok';
  return (
    <div className="usage-row">
      <span className="usage-label">{label}</span>
      <ProgressBar value={v || 0} max={100} tone={tone} label={`${label} ${v == null ? 'unknown' : `${Math.round(v)}%`}`} />
      <span className="usage-value num">{v == null ? '—' : `${Math.round(v)}%`}</span>
    </div>
  );
}

function ServiceTile({ svc, onOpen }) {
  const { health, reasons } = healthWithReasons(svc);
  return (
    <div className={`card service-tile service-tile-${health}`}>
      <div className="service-tile-head">
        <h3 className="service-tile-name">
          <button type="button" className="stretched" onClick={() => onOpen(svc.id)}
                  aria-label={`${svc.id}, ${HEALTH_LABEL[health]}. Open service details`}>
            {svc.id}
          </button>
        </h3>
        <HealthBadge health={health} />
      </div>
      <p className="service-tile-role">{svc.role}</p>
      <div className="row row-wrap gap-2 service-tile-meta">
        <Badge tone="outline">{svc.runtime === 'ec2' ? 'EC2' : 'Fargate'}</Badge>
        <span className={svc.running < svc.desired ? 'text-danger fw-600' : 'text-muted'}>
          {svc.running}/{svc.desired} {svc.desired === 1 ? 'replica' : 'replicas'}
        </span>
        {svc.requests_per_min != null && (
          <span className="text-muted" title="Average over the last 10 minutes, health checks left out">
            {rate(svc.requests_per_min)} req/min, 10 min avg
          </span>
        )}
        {svc.routed === false && <Badge tone="warn">Not routed</Badge>}
      </div>
      <div className="stack gap-1">
        <UsageBar label="CPU" value={svc.cpu_pct} warn={THRESHOLDS.cpuWarn} crit={THRESHOLDS.cpuCrit} />
        <UsageBar label="Memory" value={svc.mem_pct} warn={THRESHOLDS.memWarn} crit={THRESHOLDS.memCrit} />
      </div>
      <div className="service-tile-spark" aria-hidden="true">
        <Sparkline data={(svc.spark || []).map((v, i) => ({ label: String(i), value: v }))}
                   color={health === 'critical' ? 'var(--danger)' : health === 'warn' ? 'var(--warn)' : 'var(--accent-text)'}
                   height={28} />
      </div>
      <div className="service-tile-foot">
        <span className="mono text-xs">{svc.image?.tag || '—'}</span>
        {svc.image?.stale
          ? <Badge tone="warn" icon="alert">Behind :latest</Badge>
          : <span className="text-xs text-faint">image current</span>}
      </div>
      {reasons.length > 0 && health !== 'idle' && (
        <p className="service-tile-reason text-xs">{reasons.join(' · ')}</p>
      )}
    </div>
  );
}

/**
 * Everything that is not healthy, in one list: services judged by the shared
 * thresholds, firing alarms, pending agents. Empty is the normal state and says so.
 */
function Attention({ services, alarms, kubernetes, onOpen }) {
  const items = [];
  services.forEach((s) => {
    const { health, reasons } = healthWithReasons(s);
    if (health === 'warn' || health === 'critical') {
      items.push({ key: `svc-${s.id}`, tone: health === 'critical' ? 'danger' : 'warn',
        title: s.id, detail: reasons.join(', '), service: s.id });
    }
  });
  (alarms?.items || []).filter(a => a.state === 'ALARM').forEach((a) => {
    items.push({ key: `al-${a.name}`, tone: 'danger', title: a.name,
      detail: `alarm firing, ${ago(a.since)}`, service: a.service });
  });
  if (kubernetes?.agents_pending > 0) {
    items.push({ key: 'k8s-pending', tone: 'warn', title: 'Agent pods pending',
      detail: `${kubernetes.agents_pending} waiting for a node` });
  }
  if (items.length === 0) {
    return (
      <div className="notice notice-ok mb-4" role="status">
        <Icon name="check" />
        <div className="notice-body">Nothing needs attention: every service is within its thresholds and no alarm is firing.</div>
      </div>
    );
  }
  items.sort((a, b) => (a.tone === b.tone ? 0 : a.tone === 'danger' ? -1 : 1));
  return (
    <div className="card attention-card mb-4">
      <div className="tile-head">
        <div className="tile-head-text">
          <h3 className="tile-title">Needs attention</h3>
          <div className="tile-subtitle">{items.length} {items.length === 1 ? 'item' : 'items'}, most severe first</div>
        </div>
      </div>
      <ul className="attention-list">
        {items.map(it => (
          <li key={it.key}>
            <span className={`dot health-dot-${it.tone === 'danger' ? 'critical' : 'warn'}`} aria-hidden="true" />
            <span className="fw-600">{it.title}</span>
            <span className="text-muted">{it.detail}</span>
            {it.service && (
              <button type="button" className="btn btn-ghost btn-sm ml-auto" onClick={() => onOpen(it.service)}>
                Open
              </button>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function OverviewPanel({ overview, onOpenService }) {
  const services = useMemo(() => overview?.services || [], [overview]);
  const judged = useMemo(() => services.map(s => healthWithReasons(s).health), [services]);
  if (!overview) return null;
  const k = overview.kubernetes || {};
  const db = overview.database || {};
  const alarms = overview.alarms || { open: 0, items: [] };
  const ok = judged.filter(h => h === 'ok' || h === 'idle').length;
  const warn = judged.filter(h => h === 'warn').length;
  const crit = judged.filter(h => h === 'critical').length;
  const running = services.reduce((s, x) => s + (x.running || 0), 0);
  const desired = services.reduce((s, x) => s + (x.desired || 0), 0);
  const firing = (alarms.items || []).filter(a => a.state === 'ALARM');

  return (
    <>
      <div className="dashboard-grid">
        <StatTile span={2} icon="server" label="Services" value={`${ok}/${services.length}`}
          sub={crit ? `${crit} critical · ${warn} degraded` : warn ? `${warn} degraded` : 'all healthy'}
          tone={crit ? 'negative' : warn ? 'warning' : 'positive'} />
        <StatTile span={2} icon="activity" label="Replicas" value={`${running}/${desired}`}
          sub="running of desired" tone={running < desired ? 'negative' : undefined} />
        <StatTile span={2} icon="users" label="Agents live" value={formatNumber(k.agents_running || 0)}
          sub={`${k.agents_pending || 0} pending · ${formatNumber(k.agents_hibernated || 0)} hibernated`}
          tone={k.agents_pending ? 'warning' : undefined} />
        <StatTile span={2} icon="infrastructure" label="Cluster nodes" value={`${k.nodes_ready ?? '—'}/${k.nodes_total ?? '—'}`}
          sub={k.karpenter_cpu_limit ? `${k.karpenter_cpu_used} of ${k.karpenter_cpu_limit} vCPU (Karpenter)` : 'ready of total'} />
        <StatTile span={2} icon="database" label="Aurora"
          value={db.acu_writer != null ? `${db.acu_writer} ACU` : (db.writer_class && db.writer_class !== 'db.serverless' ? db.writer_class : '—')}
          sub={`${formatNumber(db.connections || 0)} of ${formatNumber(db.max_connections || 0)} connections`} />
        <StatTile span={2} icon="alarm" label="Open alarms" value={String(firing.length)}
          sub={firing.length ? firing[0].name : 'none firing'}
          tone={firing.length ? 'negative' : 'positive'} />
      </div>

      <Attention services={services} alarms={alarms} kubernetes={k} onOpen={onOpenService} />

      <div className="section-head">
        <h2>Architecture</h2>
        <p>request paths, coloured by the health of each service</p>
      </div>
      <div className="card mb-4 arch-card">
        <ArchitectureMap services={services} kubernetes={k} database={db} onOpen={onOpenService} />
      </div>

      {KIND_ORDER.map((kind) => {
        const list = services.filter(s => s.kind === kind);
        if (list.length === 0) return null;
        return (
          <section key={kind} aria-labelledby={`svc-kind-${kind}`}>
            <div className="section-head">
              <h2 id={`svc-kind-${kind}`}>{KIND_GROUP_LABEL[kind]}</h2>
              <p>{KIND_LEDE[kind]}</p>
            </div>
            <div className="service-grid mb-4">
              {list.map(s => <ServiceTile key={s.id} svc={s} onOpen={onOpenService} />)}
            </div>
          </section>
        );
      })}
    </>
  );
}
